import {css, html, LitElement} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {isMobile} from './utils';

@customElement('project-page')
export class ProjectPageElement extends LitElement {
  static get styles() {
    return css`
      :host {
        left: 0;
        position: absolute;
        text-align: center;
        top: 0;
        width: 100%;
      }

      .container {
        background: rgba(255, 255, 255, 0.96);
        min-height: 100vh;
        overflow: auto;
        transition: background 0.3s ease;
        padding-bottom: 6rem;
      }

      .page {
        margin: 0 auto;
        max-width: 680px;
        padding: 0 1rem;
      }

      .project-name {
        font-size: 2rem;
        font-weight: 700;
        line-height: 3rem;
        margin: 2rem 0;
      }

      .project-image {
        max-height: 500px;
        object-fit: contain;
      }

      p {
        font-size: 1rem;
        line-height: 1.5rem;
        margin: 1rem 0;
        text-align: left;
      }

      .button,
      .button:hover,
      .button:visited {
        background: var(--link-color);
        border-radius: 2rem;
        color: #fff !important;
        display: inline-block;
        font-family: Helvetica, sans-serif;
        font-weight: 700;
        line-height: 2rem;
        margin: 1.5rem 0;
        padding: 0.5rem 2rem;
        text-decoration: none;
        text-transform: uppercase;
        transition: box-shadow 0.3s;
      }

      .button:hover {
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.8);
      }

      iframe,
      video {
        width: 100%;
      }

      .content {
        text-align: left;
      }

      .close {
        position: fixed;
        right: 1rem;
        top: 5rem;
      }

      .close svg {
        height: 36px;
        width: 36px;
      }

      a,
      a:visited,
      a:focus {
        color: var(--link-color);
      }

      a:hover {
        color: #ff0;
      }

      li {
        line-height: 1.5rem;
      }

      @media only screen and (min-width: 600px) {
        .close {
          right: 3rem;
          top: 5rem;
        }
        .close svg {
          height: 72px;
          width: 72px;
        }
      }
    `;
  }

  @property({type: Object})
  project: {id: string; title: string; content: string} | undefined;

  @property({type: Number})
  imageX = 0;

  @property({type: Number})
  imageY = 0;

  @property({type: Number})
  imageW = 0;

  @property({type: Number})
  imageH = 0;

  lastWindowWidth = window.innerWidth;
  lastWindowHeight = window.innerHeight;

  protected firstUpdated() {
    window.addEventListener(
      'resize',
      () => {
        if (this.lastWindowWidth !== window.innerWidth) {
          this.imageX = 0;
          this.imageY = 0;
          this.imageW = 0;
          this.imageH = 0;
        }
        this.lastWindowWidth = window.innerWidth;
        this.lastWindowHeight = window.innerHeight;
      },
      false
    );
  }

  _imagePositionSpecified() {
    return (
      this.imageX > 0 && this.imageY > 0 && this.imageW > 0 && this.imageH > 0
    );
  }

  protected render() {
    const imageStyle = this._imagePositionSpecified()
      ? `
            position: absolute; left: ${this.imageX}px; top: ${this.imageY}px; width: ${this.imageW}px; height: ${this.imageH}px;
        `
      : `
            margin-top: ${
              isMobile() ? 120 : 200
            }px; max-width: 100%; width: 480px;
        `;
    const contentStyle = this._imagePositionSpecified()
      ? `
            padding-top: ${this.imageY + this.imageH}px;
        `
      : '';

    return html`
      <div class="container">
        ${this.project
          ? html`
              <div class="page">
                <img
                  src="/physics-images/${this.project.id}.png"
                  class="project-image"
                  style="${imageStyle}"
                />
                <div class="content" style="${contentStyle}">
                  <h1>${this.project.title}</h1>
                  ${unsafeHTML(this.project.content)}
                </div>
              </div>
            `
          : ''}
      </div>
      <page-footer></page-footer>
      <app-link
        class="close"
        href="${this._imagePositionSpecified() ? '/' : '/projects/'}"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="24"
          viewBox="0 0 24 24"
          width="24"
        >
          <path d="M0 0h24v24H0z" fill="none" />
          <path
            fill="#222"
            d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
          />
        </svg>
      </app-link>
    `;
  }
}
